import React from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { getSuppliers } from 'reducers';

import TableWrapper from 'components/Shared/TableWrapper';
import ActiveInvoicingFlags from 'components/Clients/ActiveInvoicingFlags';
import Avatar from '@material-ui/core/Avatar';

import logoImg from 'assets/default-logo.svg';
import { ReactComponent as AddButtonIcon } from 'assets/add-button-icon.svg';
import { ReactComponent as ExportIcon } from 'assets/export-button-icon.svg';

import { usePreloadTable } from 'utils/hooks';
import { getStateAgencyName } from 'utils/helpers';

import {
    TABLE_COLUMNS,
    DEFAULT_TABLE_COLUMNS,
    TABLE_FILTERS,
    SORT_QUERY_SELECTOR,
    PAGE_QUERY_SELECTOR,
    SEARCH_QUERY_SELECTOR,
    DEFAULT_ITEMS_PER_PAGE,
    TABLE_INITIAL_QUERY,
    TABLE_QUERY_MAP,
} from './SuppliersTableConstants';
import { ACTIVE_INVOICING_FILTER_NAME } from 'constants/supplierConstants';

const SuppliersTable = ({
    suppliersAccountManagers,
    tableFetchAction,
    tablePrefetchAction,
    reload,
    setReload,
    getActionsHandler,
    handleOpenAddDialog,
    handleExportSuppliers,
    updateSupplierFlag,
}) => {
    const intl = useIntl();

    const dataSelector = (state, page) => getSuppliers(state, page - 1, DEFAULT_ITEMS_PER_PAGE);

    TABLE_FILTERS[1].value = suppliersAccountManagers.map(x => x.name);
    const [isTableDataLoading, totalCount] = usePreloadTable(
        TABLE_INITIAL_QUERY,
        TABLE_QUERY_MAP,
        TABLE_FILTERS,
        tableFetchAction,
        reload,
        setReload,
        tablePrefetchAction,
    );

    const generateToString = (id) => {
        return `/${getStateAgencyName()}/partners/${id}/team/people`;
    };

    const adjustSupplierKeys = stateData => stateData.map(supplier => {
        return {
            _id: supplier._id,
            name: (
                <Link to={generateToString(supplier._id)}>
                    <div className="logo-name-wrapper">
                        <Avatar
                            alt="Partner Avatar"
                            className="avatar-logo-image"
                            src={supplier.logoImgUrl ? supplier.logoImgUrl : logoImg}
                        />
                        <div className="name-text">
                            {supplier.name}
                        </div>
                    </div>
                </Link>
            ),
            contactPerson: supplier.contactPerson || '-',
            accountManager: (
                <div className="logo-name-wrapper">
                    {supplier.accountManager.profilePictureImgUrl &&
                        <Avatar
                            alt="Account Manager Avatar"
                            className="avatar-logo-image"
                            src={supplier.accountManager.profilePictureImgUrl}
                        />
                    }
                    <div className="name-text">
                        {supplier.accountManager?.name || '-'}
                    </div>
                </div>
            ),
            invoiceStatus: (
                <>
                    {supplier.invoiceStatus === ACTIVE_INVOICING_FILTER_NAME ?
                        <ActiveInvoicingFlags
                            flag={supplier.flag}
                            userId={supplier._id}
                            updateFlag={updateSupplierFlag}
                        />
                        : supplier.invoiceStatus || '-'
                    }
                </>
            ),
            item: supplier,
        };
    });

    return (
        <div className="suppliers-table-container">
            <TableWrapper
                title={intl.formatMessage({ id: "partners-section-title" })}
                columns={TABLE_COLUMNS}
                defaultColumns={DEFAULT_TABLE_COLUMNS}
                dataSelector={dataSelector}
                sortQuerySelector={SORT_QUERY_SELECTOR}
                adjustKeysFunction={adjustSupplierKeys}
                isTableDataLoading={isTableDataLoading}
                withPagination
                withActions
                getActionsHandler={getActionsHandler}
                addMargin
                pagesCount={Math.ceil(totalCount / DEFAULT_ITEMS_PER_PAGE)}
                pageQuerySelector={PAGE_QUERY_SELECTOR}
            >
                <TableWrapper.TableAlignedItems>
                    <TableWrapper.TableAlignedItems.LeftAligned>
                        <TableWrapper.TableAlignedItems.TableHeader />
                        <TableWrapper.TableAlignedItems.TableFilter filters={TABLE_FILTERS} />
                        <TableWrapper.TableAlignedItems.TableSearch queryParamName={SEARCH_QUERY_SELECTOR} />
                    </TableWrapper.TableAlignedItems.LeftAligned>
                    <TableWrapper.TableAlignedItems.RightAligned>
                        <TableWrapper.TableAlignedItems.TableActionButton
                            text={intl.formatMessage({ id: "add-partner-button-text" })}
                            icon={<AddButtonIcon />}
                            handleClick={handleOpenAddDialog}
                        />
                        <TableWrapper.TableAlignedItems.TableActionButton
                            text={intl.formatMessage({ id: "export-button-text" })}
                            icon={<ExportIcon />}
                            handleClick={handleExportSuppliers.bind(null, TABLE_FILTERS)}
                        />
                    </TableWrapper.TableAlignedItems.RightAligned>
                </TableWrapper.TableAlignedItems>
                <TableWrapper.TableContent />
            </TableWrapper>
        </div>
    );
};

export default SuppliersTable;
